import {
  AlertTriangle,
  Bladder,
  Gallbladder,
  GeneralSurgery,
  HeartOrgan,
  IDocumentsAccepted,
  IDocumentsDenied,
  Joints,
  Kidneys,
  Liver,
  Lungs,
  Nerve,
  Orthopaedics,
  Pregnant,
  Radiology,
  StarLarge,
  Stethoscope,
  Syringe,
  UltrasoundScanner,
  VascularSurgery,
} from "healthicons-react/filled";
import type { FC, SVGProps } from "react";
import {
  type ExamTypeCategory,
  US_EXAM_TYPE_CATEGORY,
} from "../../../global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import {
  type ExamType,
  US_EXAM_TYPES,
  type UsExamType,
} from "../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";

export type HealthIconComponent = FC<SVGProps<SVGSVGElement>>;

const US_CATEGORY_ICONS: Record<
  Exclude<ExamTypeCategory, "procedures">,
  HealthIconComponent
> = {
  emergency: UltrasoundScanner,
  abdominal_renal: Kidneys,
  vascular: VascularSurgery,
  msk_soft_tissue: Orthopaedics,
  obstetric: Pregnant,
};

const US_EXAM_ICONS = Object.fromEntries(
  US_EXAM_TYPES.map((examType) => [
    examType,
    US_CATEGORY_ICONS[US_EXAM_TYPE_CATEGORY[examType]],
  ]),
) as Record<UsExamType, HealthIconComponent>;

// Exam type → healthicon component.
// Keys must stay in sync with the ExamType union.
export const EXAM_ICONS: Record<ExamType, HealthIconComponent> = {
  efast: UltrasoundScanner,
  cardiac: HeartOrgan,
  lung: Lungs,
  bladder: Bladder,
  renal: Kidneys,
  aorta: VascularSurgery,
  pregnancy: Pregnant,
  msk: Orthopaedics,
  guided_joint_injection: Joints,
  guided_tendon_injection: Syringe,
  guided_nerve_block: Nerve,
  ...US_EXAM_ICONS,
};

// Pre-emptive mappings — add to EXAM_ICONS once ExamType is extended:
//   gallbladder: Gallbladder    (body/gallbladder)
//   liver:       Liver          (body/liver)
//   general:     GeneralSurgery (specialties/general-surgery)
export const FUTURE_EXAM_ICONS = {
  gallbladder: Gallbladder,
  liver: Liver,
  general: GeneralSurgery,
} as const;

// Stat card icons — ordered to match STAT_LABELS in home/page.tsx:
//   "TODAY'S SCANS" | "PENDING REVIEW" | "TOTAL ENCOUNTERS" | "AVG QA SCORE"
export const STAT_ICON_COMPONENTS = [
  UltrasoundScanner,
  Radiology,
  Stethoscope,
  StarLarge,
] as const;

// Misc UI icons re-exported for a consistent import path across the ePocus subtree.
export {
  AlertTriangle,
  IDocumentsAccepted,
  IDocumentsDenied,
  Stethoscope,
  UltrasoundScanner,
};
